<template>
    <div>
        <div class="video-generator-container">
            <div class="main-content">
                <!-- 左侧视频列表 -->
                <div class="video-list-section">
                    <div class="video-list-header">
                        <h2 class="video-list-title">我的视频</h2>
                    </div>
                    <div class="video-list-content">
                        <!-- 视频网格 -->
                        <div v-if="videoList.length > 0" class="video-grid" v-infinite-scroll="getList">
                            <div 
                                v-for="videoItem in videoList" 
                                :key="videoItem.id" 
                                class="video-card"
                            >
                                <div 
                                    class="video-thumbnail"
                                    :class="{ generating: videoItem.state === 'RUNNING' }"
                                    @click="handleVideoClick(videoItem)"
                                >
                                    <i v-if="videoItem.state === 'SUCCEEDED'" class="el-icon-video-play"></i>
                                    <i v-else-if="videoItem.state === 'PENDING' || videoItem.state === 'RUNNING'" class="el-icon-loading"></i>
                                    <i v-else class="el-icon-warning"></i>
                                    
                                    <!-- 操作按钮 -->
                                    <div v-if="videoItem.state === 'SUCCEEDED'" class="video-actions">
                                        <button 
                                            class="action-btn" 
                                            title="下载视频"
                                            @click.stop="handleDownload(videoItem)"
                                        >
                                            <i class="el-icon-download"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="video-info">
                                    <div class="video-title">{{ videoItem.prompt }}</div>
                                    <div class="video-meta">
                                        <span>{{ videoItem.duration }}秒</span>
                                        <span v-if="videoItem.resolution" class="video-ratio">{{ getResolutionText(videoItem.resolution) }}</span>
                                        <span 
                                            class="video-status"
                                            :class="{
                                                'status-completed': videoItem.state === 'completed',
                                                'status-generating': videoItem.state === 'generating',
                                                'status-failed': videoItem.state === 'failed'
                                            }"
                                        >
                                            {{ getStatusText(videoItem.state) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 空状态 -->
                        <div v-else class="empty-state">
                            <div class="empty-icon">
                                <i class="el-icon-film"></i>
                            </div>
                            <div class="empty-text">还没有生成任何视频</div>
                            <div class="empty-subtext">使用右侧面板开始创建你的第一个AI视频吧</div>
                        </div>
                    </div>
                </div>
                
                <!-- 右侧控制面板 -->
                <div class="control-panel">
                    <div class="panel-header">
                        <h3 class="panel-title">创建视频</h3>
                    </div>
                    
                    <div class="panel-content">
                        <!-- Tab切换 -->
                        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
                            <!-- 文生视频 -->
                            <el-tab-pane label="文生视频" name="text2video">
                                <div class="form-section">
                                    <label class="form-label">视频描述</label>
                                    <el-input
                                        v-model="textForm.prompt"
                                        type="textarea"
                                        :rows="4"
                                        placeholder="请详细描述你想要生成的视频内容..."
                                        maxlength="100"
                                        show-word-limit
                                    ></el-input>
                                    
                                    <!-- 示例提示 -->
                                    <div class="example-section">
                                        <div class="example-header">
                                            <i class="el-icon-magic-stick"></i>
                                            <span>精选示例</span>
                                            <el-tooltip content="点击示例快速填入描述" placement="top">
                                                <i class="el-icon-question"></i>
                                            </el-tooltip>
                                        </div>
                                        <div class="example-grid">
                                            <div 
                                                v-for="example in textExamples" 
                                                :key="example.id"
                                                class="example-item"
                                                @click="applyExample(example.text, 'text')"
                                            >
                                                <div class="example-icon">{{ example.icon }}</div>
                                                <div class="example-text">{{ example.text }}</div>
                                                <div class="example-category">{{ example.category }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="form-section">
                                    <label class="form-label">视频时长</label>
                                    <el-radio-group v-model="textForm.duration" class="radio-group" >
                                        <el-radio-button label="5" border>5秒</el-radio-button>
                                        <!-- <el-radio-button label="10" border>10秒</el-radio-button> -->
                                    </el-radio-group>
                                </div>
                                
                                <div class="form-section">
                                    <label class="form-label">视频比例</label>
                                    <div class="aspect-ratio-options">
                                        <div 
                                            class="aspect-ratio-option"
                                            :class="{ active: textForm.resolution === '1920*1080' }"
                                            @click="textForm.resolution = '1920*1080'"
                                        >
                                            <div>16:9</div>
                                            <div style="font-size: 10px; color: #999;">横屏</div>
                                        </div>
                                        <div 
                                            class="aspect-ratio-option"
                                            :class="{ active: textForm.resolution === '1080*1920' }"
                                            @click="textForm.resolution = '1080*1920'"
                                        >
                                            <div>9:16</div>
                                            <div style="font-size: 10px; color: #999;">竖屏</div>
                                        </div>
                                        <div 
                                            class="aspect-ratio-option"
                                            :class="{ active: textForm.resolution === '1440*1440' }"
                                            @click="textForm.resolution = '1440*1440'"
                                        >
                                            <div>1:1</div>
                                            <div style="font-size: 10px; color: #999;">方形</div>
                                        </div>
                                    </div>
                                </div>
                            </el-tab-pane>
                            
                            <!-- 图生视频 -->
                            <!-- <el-tab-pane label="图生视频" name="image2video">
                                <div class="form-section">
                                    <label class="form-label">上传图片</label>
                                    <div 
                                        class="upload-area"
                                        :class="{ 'has-image': imageForm.image }"
                                        @click="handleImageUpload"
                                    >
                                        <div v-if="!imageForm.image">
                                            <i class="el-icon-plus upload-icon"></i>
                                            <div class="upload-text">点击上传图片</div>
                                            <div style="font-size: 12px; color: #999; margin-top: 5px;">
                                                支持 JPG、PNG 格式，最大 10MB
                                            </div>
                                        </div>
                                        <div v-else>
                                            <img :src="imageForm.imagePreview" alt="预览图" class="uploaded-image">
                                            <div class="upload-text">点击更换图片</div>
                                        </div>
                                    </div>
                                    <input 
                                        ref="imageInput" 
                                        type="file" 
                                        accept="image/*" 
                                        style="display: none;"
                                        @change="handleImageChange"
                                    >
                                </div>
                                
                                <div class="form-section">
                                    <label class="form-label">视频描述</label>
                                    <el-input
                                        v-model="imageForm.prompt"
                                        type="textarea"
                                        :rows="3"
                                        placeholder="描述图片中物体的运动方式..."
                                        maxlength="100"
                                        show-word-limit
                                    ></el-input>
                                    
                                   
                                    <div class="example-section">
                                        <div class="example-header">
                                            <i class="el-icon-magic-stick"></i>
                                            <span>运动示例</span>
                                        </div>
                                        <div class="example-tags">
                                            <span 
                                                v-for="example in imageExamples" 
                                                :key="example.id"
                                                class="example-tag"
                                                @click="applyExample(example.text, 'image')"
                                            >
                                                {{ example.text }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="form-section">
                                    <label class="form-label">视频时长</label>
                                    <el-radio-group v-model="imageForm.duration" class="radio-group">
                                        <el-radio-button label="5">5秒</el-radio-button>
                                        <el-radio-button label="10">10秒</el-radio-button>
                                    </el-radio-group>
                                </div>
                            </el-tab-pane> -->
                        </el-tabs>
                    </div>
                    
                    <div class="panel-footer">
                        <el-button 
                            type="primary" 
                            class="generate-button"
                            :loading="generating"
                            @click="handleGenerate"
                        >
                            {{ generating ? '生成中...' : '立即生成' }}
                        </el-button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 视频播放器弹窗 -->
        <div v-if="showVideoPlayer" class="video-player-modal" @click="closeVideoPlayer">
            <div class="video-player-content" @click.stop>
                <div class="player-header">
                    <div class="player-title">{{ currentVideo.prompt }}</div>
                    <button class="close-btn" @click="closeVideoPlayer">
                        <i class="el-icon-close"></i>
                    </button>
                </div>
                <video 
                    class="video-player"
                    controls
                    autoplay
                    controlsList="nodownload noplaybackrate"
                    :src="getVideoUrl(currentVideo)"
                    v-if="showVideoPlayer"
                >
                    您的浏览器不支持视频播放
                </video>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            page: 1,
            loading: false,
            finished: false,
            activeTab: 'text2video',
            generating: false,
            showVideoPlayer: false,
            currentVideo: null,
            textForm: {
                prompt: '',
                duration: '5',
                resolution: '1920*1080'
            },
            imageForm: {
                image: null,
                imagePreview: '',
                prompt: '',
                duration: '5'
            },
            // 文生视频示例
            textExamples: [
                {
                    id: 1,
                    icon: '🐱',
                    text: '一只橘色小猫在阳光下的花园里追逐飞舞的蝴蝶，花朵盛开，微风轻拂',
                    category: '动物'
                },
                {
                    id: 2,
                    icon: '🌊',
                    text: '蔚蓝的海浪轻柔地拍打着金色沙滩，夕阳西下，海鸥在天空中翱翔',
                    category: '自然'
                },
                {
                    id: 3,
                    icon: '🌃',
                    text: '繁华都市夜景，摩天大楼的霓虹灯闪烁，车流如水般在街道上流淌',
                    category: '城市'
                },
                {
                    id: 4,
                    icon: '🚗',
                    text: '红色跑车在蜿蜒山路上疾驰，两旁是郁郁葱葱的森林和远山',
                    category: '交通'
                },
                {
                    id: 5,
                    icon: '🎨',
                    text: '抽象色彩在画布上缓缓流淌混合，形成梦幻般的渐变效果',
                    category: '艺术'
                },
                {
                    id: 6,
                    icon: '☁️',
                    text: '白云在蓝天中缓缓飘过，阳光透过云层洒向大地，光影变化',
                    category: '天空'
                }
            ],
            // 图生视频示例
            imageExamples: [
                { id: 1, text: '水中倒影轻微波动' },
                { id: 2, text: '头发在微风中飘动' },
                { id: 3, text: '烟雾向上缓缓升起' },
                { id: 4, text: '树叶在风中轻摆' },
                { id: 5, text: '瀑布水流向下流淌' },
                { id: 6, text: '云朵在天空中移动' },
                { id: 7, text: '火焰跳跃燃烧' },
                { id: 8, text: '花瓣在风中飞舞' }
            ],
            videoList: []
        }
    },
    created(){
        this.getList()
    },
    methods: {
        async getList(){
            if (this.loading || this.finished) return
            this.loading = true
            try {
                const res = await this.$api.post('/api/production/videoGendrationList', {page:this.page})
                const list = res.data.list.data || []
                if (list.length > 0) {
                    this.videoList.push(...list)
                    this.page++
                } else {
                    this.finished = true
                }
            } catch (err) {
                console.error('加载图片失败：', err)
            } finally {
                this.loading = false
            }
        },

        handleTabClick(tab) {
            console.log('切换到:', tab.name);
        },
        
        // 应用示例到输入框
        applyExample(text, type) {
            if (type === 'text') {
                this.textForm.prompt = text;
            } else {
                this.imageForm.prompt = text;
            }
            this.$message.success('示例已应用，你可以继续编辑');
        },
        
        handleVideoClick(video) {
            if (video.state === 'SUCCEEDED') {
                this.currentVideo = video;
                this.showVideoPlayer = true;
            }
        },
        
        closeVideoPlayer() {
            this.showVideoPlayer = false;
            this.currentVideo = null;
        },
        
        handleDownload(video) {
            // 模拟下载功能
            this.$message.success('开始下载视频...');
            
            // 实际项目中这里应该是真实的下载链接
            const link = document.createElement('a');
            link.href = this.getVideoUrl(video);
            link.download = `video_${video.id}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        
        getVideoUrl(video) {
            // 这里返回模拟的视频URL，实际项目中应该是真实的视频地址
            // 使用一个示例视频URL作为演示
            return video.video;
        },
        
        handleImageUpload() {
            this.$refs.imageInput.click();
        },
        
        handleImageChange(event) {
            const file = event.target.files[0];
            if (file) {
                // 验证文件类型
                if (!file.type.startsWith('image/')) {
                    this.$message.error('请上传图片文件');
                    return;
                }
                
                // 验证文件大小
                if (file.size > 10 * 1024 * 1024) {
                    this.$message.error('图片大小不能超过 10MB');
                    return;
                }
                
                this.imageForm.image = file;
                
                // 创建预览
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.imageForm.imagePreview = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        },
        
        async handleGenerate() {
            // 验证表单
            if (this.activeTab === 'text2video') {
                if (!this.textForm.prompt.trim()) {
                    this.$message.error('请输入视频描述');
                    return;
                }
            } else {
                if (!this.imageForm.image) {
                    this.$message.error('请上传图片');
                    return;
                }
                if (!this.imageForm.prompt.trim()) {
                    this.$message.error('请输入视频描述');
                    return;
                }
            }
            
            this.generating = true;
            
            const res = await this.$api.post('/api/production/videoGeneration', this.textForm)
            if(res.code == 1){
                this.generating = false;
                // 创建新的视频记录
                const newVideo = {
                    prompt: this.activeTab === 'text2video' 
                        ? this.textForm.prompt 
                        : this.imageForm.prompt,
                    duration: parseInt(this.activeTab === 'text2video' 
                        ? this.textForm.duration 
                        : this.imageForm.duration),
                    resolution: this.activeTab === 'text2video' 
                        ? this.textForm.resolution 
                        : null,
                    state: 'PENDING',
                    type: this.activeTab,
                    createtime_text: '刚刚'
                };
                // 添加到视频列表顶部
                this.videoList.unshift(newVideo);

                // 重置表单
                if (this.activeTab === 'text2video') {
                    this.textForm = {
                        prompt: '',
                        duration: '5',
                        resolution: '1920*1080'
                    };
                } else {
                    this.imageForm = {
                        image: null,
                        imagePreview: '',
                        prompt: '',
                        duration: '5'
                    };
                }
                this.$message.success(res.msg || '视频生成任务已提交，请稍候查看结果');
                // 模拟生成完成
                // setTimeout(() => {
                //     const video = this.videoList.find(v => v.id === newVideo.id);
                //     if (video) {
                //         video.state = Math.random() > 0.2 ? 'completed' : 'failed';
                //     }
                // }, 10000);
            }else{
                this.$message.error(res.msg);
            }
        },
        
        getStatusText(status) {
            const statusMap = {
                'SUCCEEDED': '已完成',
                'PENDING': '排队中',
                'RUNNING': '生成中',
                'FAILED': '生成失败',
                'CANCELED': '任务已取消'
            };
            return statusMap[status] || '未知状态';
        },

        getResolutionText(resolution) {
            const resolutionMap = {
                '1920*1080': '16:9',
                '1080*1920': '9:16',
                '1440*1440': '1:1'
            };
            return resolutionMap[resolution] || '16:9';
        }
    }
};
</script>

<style scoped>
    .video-generator-container {
        padding: 20px;
        /* height: 100vh; */
        overflow: hidden;
    }
    
    .main-content {
        display: flex;
        gap: 20px;
        height: calc(100vh - 40px);
    }
    
    .video-list-section {
        flex: 0 0 70%;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }
    
    .video-list-header {
        padding: 20px;
        border-bottom: 1px solid #ebeef5;
        background: #fff;
    }
    
    .video-list-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0;
    }
    
    .video-list-content {
        height: calc(100% - 105px);
        overflow-y: auto;
        padding: 20px;
    }
    
    .video-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 20px;
    }
    
    .video-card {
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.3s;
        background: white;
        position: relative;
    }
    
    .video-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
    
    .video-card:hover .video-actions {
        opacity: 1;
    }
    
    .video-thumbnail {
        width: 100%;
        height: 160px;
        background: linear-gradient(135deg, #667eea 0%, #87cfdb 100%);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 24px;
        cursor: pointer;
    }
    
    .video-actions {
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        gap: 5px;
        opacity: 0;
        transition: opacity 0.3s;
    }
    
    .action-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.6);
        color: white;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        font-size: 14px;
    }
    
    .action-btn:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: scale(1.1);
    }
    
    .video-player-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
    }
    
    .video-player-content {
        background: white;
        border-radius: 12px;
        padding: 20px;
        max-width: 90vw;
        max-height: 90vh;
        width: auto;
        height: auto;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    
    .player-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid #ebeef5;
        width: 100%;
    }
    
    .player-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        max-width: 600px;
        /* overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap; */
    }
    
    .close-btn {
        width: 30px;
        height: 30px;
        border: none;
        background: #f5f7fa;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #909399;
        transition: all 0.3s;
    }
    
    .close-btn:hover {
        background: #e4e7ed;
        color: #606266;
    }
    
    .video-player {
        max-width: 100%;
        max-height: 70vh;
        height: auto;
        width: auto;
        object-fit: contain;
        border-radius: 8px;
    }
    
    .video-thumbnail.generating {
        background: linear-gradient(45deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3);
        background-size: 400% 400%;
        animation: gradientShift 2s ease infinite;
    }
    
    @keyframes gradientShift {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
    }
    
    .video-info {
        padding: 15px;
    }
    
    .video-title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 8px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    
    .video-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        color: #909399;
    }
    
    .video-status {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 500;
    }
    
    .status-completed {
        background: #f0f9ff;
        color: #67c23a;
    }
    
    .status-generating {
        background: #fff7e6;
        color: #e6a23c;
    }
    
    .status-failed {
        background: #fef0f0;
        color: #f56c6c;
    }
    
    .control-panel {
        flex: 0 0 30%;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
    }
    
    .panel-header {
        padding: 20px;
        border-bottom: 1px solid #ebeef5;
    }
    
    .panel-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0;
    }
    
    .panel-content {
        flex: 1;
        padding: 20px;
        overflow-y: auto;
    }
    
    .form-section {
        margin-bottom: 25px;
    }
    
    .form-label {
        font-size: 14px;
        font-weight: 500;
        color: #606266;
        margin-bottom: 8px;
        display: block;
    }
    
    /* 示例区域样式 */
    .example-section {
        margin-top: 12px;
        padding: 15px;
        background: linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%);
        border-radius: 8px;
        border: 1px solid #e1f0ff;
    }
    
    .example-header {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 12px;
        font-size: 13px;
        font-weight: 500;
        color: #409eff;
    }
    
    .example-header i:last-child {
        margin-left: auto;
        font-size: 12px;
        color: #c0c4cc;
        cursor: help;
    }
    
    .example-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }
    
    .example-item {
        padding: 10px;
        background: white;
        border: 1px solid #e1f0ff;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s;
        text-align: center;
        position: relative;
        overflow: hidden;
    }
    
    .example-item:hover {
        border-color: #409eff;
        background: #ecf5ff;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
    }
    
    .example-icon {
        font-size: 16px;
        margin-bottom: 4px;
    }
    
    .example-text {
        font-size: 11px;
        color: #606266;
        line-height: 1.3;
        margin-bottom: 4px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    
    .example-category {
        font-size: 10px;
        color: #909399;
        background: #f5f7fa;
        padding: 1px 6px;
        border-radius: 10px;
        display: inline-block;
    }
    
    .example-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .example-tag {
        padding: 6px 12px;
        background: white;
        border: 1px solid #e1f0ff;
        border-radius: 15px;
        font-size: 12px;
        color: #606266;
        cursor: pointer;
        transition: all 0.3s;
        white-space: nowrap;
    }
    
    .example-tag:hover {
        border-color: #409eff;
        background: #ecf5ff;
        color: #409eff;
        transform: translateY(-1px);
    }
    
    .radio-group {
        display: flex;
        /* gap: 10px; */
    }
    
    .radio-group .el-radio-button__inner {
        border: 1px solid #dcdfe6;
        border-radius: 4px;
    }
    
    .radio-group .el-radio-button:first-child .el-radio-button__inner {
        border-left: 1px solid #dcdfe6;
    }
    
    .aspect-ratio-options {
        display: flex;
        gap: 10px;
    }
    
    .aspect-ratio-option {
        flex: 1;
        text-align: center;
        padding: 8px;
        border: 2px solid #e4e7ed;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s;
        font-size: 12px;
    }
    
    .aspect-ratio-option.active {
        border-color: #409eff;
        background: #ecf5ff;
        color: #409eff;
    }
    
    .upload-area {
        border: 2px dashed #d9d9d9;
        border-radius: 6px;
        padding: 40px 20px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s;
        background: #fafafa;
    }
    
    .upload-area:hover {
        border-color: #409eff;
        background: #ecf5ff;
    }
    
    .upload-area.has-image {
        border-color: #67c23a;
        background: #f0f9ff;
    }
    
    .upload-icon {
        font-size: 48px;
        color: #c0c4cc;
        margin-bottom: 10px;
    }
    
    .upload-text {
        color: #606266;
        font-size: 14px;
    }
    
    .uploaded-image {
        max-width: 100%;
        max-height: 150px;
        border-radius: 4px;
        margin-bottom: 10px;
    }
    
    .generate-button {
        width: 100%;
        height: 50px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        background: linear-gradient(135deg, #667eea 0%, #87cfdb 100%);
        border: none;
        transition: all 0.3s;
    }
    
    .generate-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
    
    .panel-footer {
        padding: 20px;
        border-top: 1px solid #ebeef5;
    }
    
    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #909399;
    }
    
    .empty-icon {
        font-size: 64px;
        color: #c0c4cc;
        margin-bottom: 20px;
    }
    
    .empty-text {
        font-size: 16px;
        margin-bottom: 10px;
    }
    
    .empty-subtext {
        font-size: 14px;
        color: #c0c4cc;
    }
</style>